import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  Button,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Skeleton,
  // Module Visualizations
  DnsModuleView,
  EmailModuleView,
  TlsModuleView,
  HeadersModuleView,
  WhoisModuleView,
  TechModuleView,
  CloudModuleView,
  AsnModuleView,
  ReposModuleView,
  CrawlHintsModuleView,
  JsAnalysisModuleView,
  FaviconModuleView,
  SubdomainsModuleView,
  PortscanModuleView,
  IpIntelModuleView,
  TyposquatModuleView,
  BruteforceModuleView,
  CloudMisconfigModuleView,
  VulnModuleView,
  ServiceProbeModuleView,
  type DnsModuleData,
  type EmailModuleData,
  type TlsModuleData,
  type HeadersModuleData,
  type WhoisModuleData,
  type TechModuleData,
  type CloudModuleData,
  type AsnModuleData,
  type ReposModuleData,
  type CrawlHintsModuleData,
  type JsAnalysisModuleData,
  type FaviconModuleData,
  type SubdomainsModuleData,
  type PortscanModuleData,
  type IpIntelModuleData,
  type TyposquatModuleData,
  type BruteforceModuleData,
  type CloudMisconfigModuleData,
  type VulnModuleData,
  type ServiceProbeModuleData,
} from "@asm/ui";
import {
  ArrowLeft,
  AlertTriangle,
  Bug,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Radar,
  Globe,
  Mail,
  Lock,
  FileCode,
  Shield,
  Cloud,
  Network,
  Code,
  GitBranch,
  FileSearch,
  Fingerprint,
  Search,
  Activity,
  AlertOctagon,
  MapPin,
  Radio,
  Settings2,
  LayoutDashboard,
  Zap,
  ChevronDown,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import { scansApi, type Scan, type ScanResult } from "@/lib/api";
import { formatModuleName } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { RiskOverview, FindingsList } from "@/components/insights";
import { AdvancedExportMenu } from "@/components/scan-details";
import { Sparkles } from "lucide-react";

// Module icon mapping
const MODULE_ICONS: Record<string, LucideIcon> = {
  dns: Globe,
  subdomains: Search,
  email: Mail,
  tls: Lock,
  headers: FileCode,
  whois: Shield,
  cloud: Cloud,
  asn: Network,
  tech: Code,
  repos: GitBranch,
  crawl_hints: FileSearch,
  js_analysis: Fingerprint,
  favicon: Fingerprint,
  typosquat: AlertOctagon,
  ipintel: MapPin,
  portscan: Radio,
  subdomains_bruteforce: Zap,
  cloud_misconfig: Shield,
  vuln: Bug,
  service_probe: Fingerprint,
};

// Module error detection helpers
// Backend stores module errors as {"error": "error message"} in results
interface ModuleError {
  hasError: boolean;
  errorMessage: string | null;
}

function getModuleError(data: unknown): ModuleError {
  if (!data || typeof data !== "object") {
    return { hasError: false, errorMessage: null };
  }
  const moduleData = data as Record<string, unknown>;
  if ("error" in moduleData && typeof moduleData.error === "string") {
    return { hasError: true, errorMessage: moduleData.error };
  }
  return { hasError: false, errorMessage: null };
}

// Extract all module errors from results
function getModuleErrors(modules: Record<string, unknown>): Array<{ module: string; error: string }> {
  const errors: Array<{ module: string; error: string }> = [];
  for (const [module, data] of Object.entries(modules)) {
    const { hasError, errorMessage } = getModuleError(data);
    if (hasError && errorMessage) {
      errors.push({ module, error: errorMessage });
    }
  }
  return errors;
}

// Severity levels for module findings
type FindingSeverity = "critical" | "high" | "medium" | "low" | "info" | "clean";

interface ModuleFindingsAnalysis {
  count: number;
  severity: FindingSeverity;
  criticalCount: number;
  highCount: number;
}

// Analyze module data to extract findings and determine severity
function analyzeModuleFindings(module: string, data: unknown): ModuleFindingsAnalysis {
  if (!data || typeof data !== "object") {
    return { count: 0, severity: "clean", criticalCount: 0, highCount: 0 };
  }

  const moduleData = data as Record<string, unknown>;
  let issues: unknown[] = [];
  let criticalCount = 0;
  let highCount = 0;

  // Extract issues array if present
  if (Array.isArray(moduleData.issues)) {
    issues = moduleData.issues;
  }

  // Module-specific analysis
  switch (module) {
    case "repos":
      // Check for exposed secrets (critical)
      if (moduleData.secrets && Array.isArray(moduleData.secrets)) {
        const secrets = moduleData.secrets as Array<{ severity?: string }>;
        criticalCount = secrets.filter(s => s.severity === "critical" || s.severity === "high").length;
        highCount = secrets.filter(s => s.severity === "medium").length;
        issues = [...issues, ...secrets];
      }
      break;

    case "tls":
      // Check certificate issues
      if (moduleData.certificates && Array.isArray(moduleData.certificates)) {
        const certs = moduleData.certificates as Array<{ is_expired?: boolean; days_until_expiry?: number }>;
        criticalCount = certs.filter(c => c.is_expired).length;
        highCount = certs.filter(c => !c.is_expired && c.days_until_expiry !== undefined && c.days_until_expiry < 30).length;
      }
      break;

    case "cloud":
      // Check for public buckets (critical)
      if (moduleData.assets && Array.isArray(moduleData.assets)) {
        const assets = moduleData.assets as Array<{ access_level?: string }>;
        criticalCount = assets.filter(a => a.access_level === "public_write").length;
        highCount = assets.filter(a => a.access_level === "public_read").length;
      }
      break;

    case "cloud_misconfig":
      // Check cloud misconfiguration findings by severity
      if (moduleData.findings && Array.isArray(moduleData.findings)) {
        const findings = moduleData.findings as Array<{ severity?: string }>;
        criticalCount = findings.filter(f => f.severity === "critical").length;
        highCount = findings.filter(f => f.severity === "high").length;
        issues = [...issues, ...findings];
      }
      break;

    case "subdomains":
      // Check for takeover risks
      if (moduleData.takeover_risks && Array.isArray(moduleData.takeover_risks)) {
        const risks = moduleData.takeover_risks as Array<{ vulnerable?: boolean }>;
        criticalCount = risks.filter(r => r.vulnerable).length;
      }
      if (moduleData.subdomains && Array.isArray(moduleData.subdomains)) {
        issues = [...issues, ...moduleData.subdomains];
      }
      break;

    case "portscan":
      // Check for open sensitive ports
      if (moduleData.open_ports && Array.isArray(moduleData.open_ports)) {
        const ports = moduleData.open_ports as Array<{ port?: number }>;
        const sensitivePorts = [21, 22, 23, 25, 3389, 5900, 1433, 3306, 5432, 27017];
        highCount = ports.filter(p => p.port && sensitivePorts.includes(p.port)).length;
        issues = [...issues, ...ports];
      }
      break;

    case "vuln": {
      // Count CVEs from total_cves and severity breakdown
      const totalCves = typeof moduleData.total_cves === "number" ? moduleData.total_cves : 0;
      if (totalCves > 0) {
        const bySeverity = moduleData.by_severity as { critical?: number; high?: number } | undefined;
        criticalCount = bySeverity?.critical ?? 0;
        highCount = bySeverity?.high ?? 0;
        issues = Array.from({ length: totalCves });
      }
      break;
    }

    case "typosquat":
      // Check for registered lookalike domains
      if (moduleData.typosquats && Array.isArray(moduleData.typosquats)) {
        const typos = moduleData.typosquats as Array<{ registered?: boolean; type?: string }>;
        criticalCount = typos.filter(t => t.registered && t.type === "homoglyph").length;
        highCount = typos.filter(t => t.registered && t.type !== "homoglyph").length;
        issues = [...issues, ...typos];
      }
      break;

    case "headers":
      // Check for missing security headers
      if (moduleData.results && Array.isArray(moduleData.results)) {
        const results = moduleData.results as Array<{ missing_headers?: string[] }>;
        for (const result of results) {
          if (result.missing_headers) {
            const critical = result.missing_headers.filter((h: string) =>
              ["strict_transport_security", "content_security_policy"].includes(h)
            );
            criticalCount += critical.length;
            highCount += result.missing_headers.length - critical.length;
          }
        }
      }
      break;

    case "email": {
      // Check email security status
      const spf = moduleData.spf as { record?: string | null } | undefined;
      const dkim = moduleData.dkim as { valid?: boolean } | undefined;
      const dmarc = moduleData.dmarc as { record?: string | null } | undefined;
      if (spf?.record === null || spf?.record === undefined) highCount++;
      if (dkim?.valid === false) highCount++;
      if (dmarc?.record === null || dmarc?.record === undefined) criticalCount++;
      break;
    }

    case "favicon": {
      // Count favicons found and check for risky known matches
      type SubdomainFavicon = {
        hash?: number;
        known_match?: { risk_level?: string };
      };

      // Count from subdomain_results (subdomains with favicons)
      const subdomainResults = moduleData.subdomain_results as SubdomainFavicon[] | undefined;
      if (subdomainResults && Array.isArray(subdomainResults)) {
        const withFavicons = subdomainResults.filter(s => s.hash);
        issues = [...issues, ...withFavicons];

        // Check for risky matches
        for (const sub of withFavicons) {
          if (sub.known_match?.risk_level === "critical") {
            criticalCount++;
          } else if (sub.known_match?.risk_level === "high") {
            highCount++;
          }
        }
      }

      // Also check main domain known_match
      const mainKnownMatch = moduleData.known_match as { risk_level?: string } | undefined;
      if (mainKnownMatch) {
        if (mainKnownMatch.risk_level === "critical") {
          criticalCount++;
        } else if (mainKnownMatch.risk_level === "high") {
          highCount++;
        }
      }

      // Include main domain favicon if found
      if (moduleData.favicon_found) {
        issues = [{ main: true }, ...issues];
      }
      break;
    }

    default:
      // Generic issue extraction
      if (moduleData.findings && Array.isArray(moduleData.findings)) {
        issues = [...issues, ...moduleData.findings];
      }
  }

  // Calculate total count (issues already includes critical/high items where applicable)
  const count = issues.length;

  // Determine overall severity
  let severity: FindingSeverity = "clean";
  if (criticalCount > 0) {
    severity = "critical";
  } else if (highCount > 0) {
    severity = "high";
  } else if (issues.length > 10) {
    severity = "medium";
  } else if (issues.length > 0) {
    severity = "low";
  } else if (count > 0) {
    severity = "info";
  }

  return { count, severity, criticalCount, highCount };
}

// Module findings badge component with severity-based styling
function ModuleFindingsBadge({ module, data }: { module: string; data: unknown }) {
  // Check if module has an error first
  const moduleError = getModuleError(data);

  if (moduleError.hasError) {
    // Show error badge
    return (
      <span
        className="
          relative ml-1.5 inline-flex items-center justify-center
          min-w-[18px] h-[18px] px-1.5
          text-[10px] font-bold
          rounded-full border overflow-hidden isolate
          bg-red-500/30 text-red-300 border-red-500/50
          shadow-[0_0_8px_rgba(239,68,68,0.4)]
        "
        title={`Error: ${moduleError.errorMessage}`}
      >
        <XCircle className="h-3 w-3" />
      </span>
    );
  }

  const analysis = analyzeModuleFindings(module, data);

  if (analysis.count === 0) {
    return null;
  }

  const severityConfig: Record<FindingSeverity, {
    bg: string;
    text: string;
    border: string;
    glow?: string;
    pulse?: boolean;
  }> = {
    critical: {
      bg: "bg-red-500/20",
      text: "text-red-400",
      border: "border-red-500/40",
      glow: "shadow-[0_0_8px_rgba(239,68,68,0.4)]",
      pulse: true,
    },
    high: {
      bg: "bg-orange-500/20",
      text: "text-orange-400",
      border: "border-orange-500/40",
      glow: "shadow-[0_0_6px_rgba(249,115,22,0.3)]",
    },
    medium: {
      bg: "bg-amber-500/15",
      text: "text-amber-400",
      border: "border-amber-500/30",
    },
    low: {
      bg: "bg-cyan-500/15",
      text: "text-cyan-400",
      border: "border-cyan-500/30",
    },
    info: {
      bg: "bg-slate-500/15",
      text: "text-slate-400",
      border: "border-slate-500/30",
    },
    clean: {
      bg: "bg-emerald-500/15",
      text: "text-emerald-400",
      border: "border-emerald-500/30",
    },
  };

  const config = severityConfig[analysis.severity];

  return (
    <span
      className={`
        relative ml-1.5 inline-flex items-center justify-center
        min-w-[18px] h-[18px] px-1.5
        text-[10px] font-bold font-mono
        rounded-full border overflow-hidden isolate
        transition-all duration-300
        ${config.bg} ${config.text} ${config.border}
        ${config.glow || ""}
        ${config.pulse ? "animate-pulse" : ""}
      `}
      title={`${analysis.criticalCount > 0 ? `${analysis.criticalCount} critical, ` : ""}${analysis.highCount > 0 ? `${analysis.highCount} high, ` : ""}${analysis.count} total`}
    >
      <span className="relative z-10">{analysis.count > 99 ? "99+" : analysis.count}</span>
    </span>
  );
}

// Status components
function StatusBadge({ status }: { status: Scan["status"] }) {
  const statusConfig: Record<Scan["status"], { icon: LucideIcon; className: string; label: string }> = {
    pending: {
      icon: Clock,
      className: "border-amber-500/30 text-amber-400 bg-amber-500/10",
      label: "Pending",
    },
    running: {
      icon: Loader2,
      className: "border-cyan-500/30 text-cyan-400 bg-cyan-500/10",
      label: "Running",
    },
    completed: {
      icon: CheckCircle2,
      className: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10",
      label: "Completed",
    },
    failed: {
      icon: XCircle,
      className: "border-red-500/30 text-red-400 bg-red-500/10",
      label: "Failed",
    },
    cancelled: {
      icon: AlertTriangle,
      className: "border-muted text-muted-foreground bg-muted/50",
      label: "Cancelled",
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={`${config.className} px-3 py-1.5`}>
      <Icon className={`h-3.5 w-3.5 mr-1.5 ${status === "running" ? "animate-spin" : ""}`} />
      {config.label}
    </Badge>
  );
}

// View toggle component
function ViewToggle({ id, currentView }: { id: string; currentView: "simple" | "advanced" }) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-muted/30 p-1">
      <Link to={`/scans/${id}`}>
        <Button
          variant={currentView === "simple" ? "default" : "ghost"}
          size="sm"
          className={`
            gap-2 rounded-md
            ${currentView === "simple"
              ? "bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
              : "hover:bg-muted"
            }
          `}
        >
          <LayoutDashboard className="h-4 w-4" />
          Simple
        </Button>
      </Link>
      <Link to={`/scans/${id}/advanced`}>
        <Button
          variant={currentView === "advanced" ? "default" : "ghost"}
          size="sm"
          className={`
            gap-2 rounded-md
            ${currentView === "advanced"
              ? "bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
              : "hover:bg-muted"
            }
          `}
        >
          <Settings2 className="h-4 w-4" />
          Advanced
        </Button>
      </Link>
    </div>
  );
}

// Progress indicator component
function ProgressIndicator({ progress }: { progress: NonNullable<Scan["progress"]> }) {
  return (
    <div className=" rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-cyan-500/20">
          <Activity className="h-5 w-5 text-cyan-400 animate-pulse" />
        </div>
        <div>
          <h3 className="font-semibold text-cyan-50">Scan in Progress</h3>
          <p className="text-sm text-cyan-400/80">
            Currently running: <span className="font-mono">{progress.current_module ? formatModuleName(progress.current_module) : "Finalizing..."}</span>
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-2 bg-muted rounded-full overflow-hidden mb-2">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full transition-all duration-500"
          style={{ width: `${progress.percent}%` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
      </div>

      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{progress.modules_completed} of {progress.modules_total} modules</span>
        <span className="font-mono text-cyan-400">{progress.percent}%</span>
      </div>
    </div>
  );
}

// ModuleResultCard removed — Overview tab now uses OverviewContent with severity-sorted modules

// Module visualization renderer - renders appropriate component based on module name
// hideHeader is true since modules are rendered within tabs that already identify them
function ModuleVisualization({ module, data, allModules }: { module: string; data: unknown; allModules?: Record<string, unknown> }) {
  // Render specialized visualization if available
  switch (module) {
    case "dns":
      return <DnsModuleView data={data as DnsModuleData} hideHeader />;
    case "email":
      return <EmailModuleView data={data as EmailModuleData} hideHeader />;
    case "tls":
      return <TlsModuleView data={data as TlsModuleData} hideHeader />;
    case "headers":
      return <HeadersModuleView data={data as HeadersModuleData} hideHeader />;
    case "whois":
      return <WhoisModuleView data={data as WhoisModuleData} hideHeader />;
    case "tech": {
      // Enrich tech view with cloud context from ASN module when available
      const asnData = allModules?.asn as Record<string, unknown> | undefined;
      const cloudContext = asnData ? {
        cloudProviders: (asnData.infrastructure_analysis as Record<string, unknown>)?.cloud_providers as string[] | undefined,
        cdnProviders: (asnData.infrastructure_analysis as Record<string, unknown>)?.cdn_providers as string[] | undefined,
        hostingProvider: asnData.hosting_provider as string | undefined,
        subdomainProviders: (asnData.subdomain_asn_map as Array<Record<string, unknown>>)?.map(s => ({
          subdomain: s.subdomain as string,
          providers: s.providers as string[],
        })),
      } : undefined;
      return <TechModuleView data={data as TechModuleData} cloudContext={cloudContext} hideHeader />;
    }
    case "cloud":
      return <CloudModuleView data={data as CloudModuleData} hideHeader />;
    case "asn":
      return <AsnModuleView data={data as AsnModuleData} hideHeader />;
    case "repos":
      return <ReposModuleView data={data as ReposModuleData} hideHeader />;
    case "crawl_hints":
      return <CrawlHintsModuleView data={data as CrawlHintsModuleData} hideHeader />;
    case "js_analysis":
      return <JsAnalysisModuleView data={data as JsAnalysisModuleData} hideHeader />;
    case "favicon":
      return <FaviconModuleView data={data as FaviconModuleData} hideHeader />;
    case "subdomains":
      return <SubdomainsModuleView data={data as SubdomainsModuleData} hideHeader />;
    case "portscan":
      return <PortscanModuleView data={data as PortscanModuleData} hideHeader />;
    case "ipintel":
      return <IpIntelModuleView data={data as IpIntelModuleData} hideHeader />;
    case "typosquat":
      return <TyposquatModuleView data={data as TyposquatModuleData} hideHeader />;
    case "subdomains_bruteforce":
      return <BruteforceModuleView data={data as BruteforceModuleData} hideHeader />;
    case "cloud_misconfig":
      return <CloudMisconfigModuleView data={data as CloudMisconfigModuleData} hideHeader />;
    case "vuln":
      return <VulnModuleView data={data as VulnModuleData} hideHeader />;
    case "service_probe":
      return <ServiceProbeModuleView data={data as ServiceProbeModuleData} hideHeader />;
    default:
      // Fallback to raw JSON for modules without custom visualization
      return (
        <div className="rounded-lg bg-muted/50 border border-border overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/30">
            <span className="text-xs text-muted-foreground font-mono">
              {module}.json
            </span>
          </div>
          <pre className="p-4 overflow-auto text-xs font-mono text-foreground max-h-[500px]">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      );
  }
}

// Business category grouping for module tabs
const MODULE_CATEGORIES: { label: string; modules: string[] }[] = [
  { label: "Email Security", modules: ["email"] },
  { label: "Website Security", modules: ["tls", "headers", "tech", "js_analysis", "crawl_hints"] },
  { label: "Domain Health", modules: ["dns", "whois", "subdomains", "subdomains_bruteforce"] },
  { label: "External Exposure", modules: ["portscan", "service_probe", "cloud", "cloud_misconfig", "asn", "ipintel", "vuln"] },
  { label: "Brand Protection", modules: ["typosquat", "repos", "favicon"] },
];

function getModuleCategoryLabel(module: string): string {
  for (const cat of MODULE_CATEGORIES) {
    if (cat.modules.includes(module)) return cat.label;
  }
  return "Other";
}

// Collapsible scan configuration card
function CollapsibleScanConfig({ scan, results, t }: { scan: Scan; results: ScanResult | undefined; t: (key: string, defaultValue: string) => string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="rounded-lg border border-border bg-card/50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-medium text-muted-foreground">{t("details.info.title", "Scan Configuration")}</h2>
          <span className="text-xs text-muted-foreground">
            {scan.mode} · {scan.modules.length} modules · {scan.completed_at ? new Date(scan.completed_at).toLocaleDateString() : "in progress"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {scan.status === "completed" && results != null && (
            <span onClick={(e) => e.stopPropagation()}>
              <AdvancedExportMenu scan={scan} results={results} />
            </span>
          )}
          {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>

      {isOpen && (
        <div className="px-4 pb-4 border-t border-border pt-4">
          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
            <div>
              <dt className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t("details.info.target", "Target")}</dt>
              <dd className="font-mono text-sm">{scan.target}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t("details.info.mode", "Mode")}</dt>
              <dd className="text-sm capitalize"><Badge variant="outline" className="text-xs">{scan.mode}</Badge></dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t("details.info.modules", "Modules")}</dt>
              <dd className="text-sm font-mono">{scan.modules.length}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t("details.info.completedAt", "Completed")}</dt>
              <dd className="text-sm">{scan.completed_at ? new Date(scan.completed_at).toLocaleString() : "—"}</dd>
            </div>
          </dl>
          <div className="flex flex-wrap gap-2">
            {scan.modules.map((module) => {
              const Icon = MODULE_ICONS[module] || FileCode;
              return (
                <Badge key={module} variant="outline" className="px-2.5 py-1 gap-1.5">
                  <Icon className="h-3 w-3" />
                  {formatModuleName(module)}
                </Badge>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Overview tab: severity-sorted module summary instead of meaningless key counts
function OverviewContent({ modules }: { modules: Record<string, unknown> }) {
  const entries = Object.entries(modules);

  // Analyze all modules
  const analyzed = entries.map(([module, data]) => {
    const error = getModuleError(data);
    const findings = analyzeModuleFindings(module, data);
    return { module, data, error, findings };
  });

  // Split into three groups
  const withIssues = analyzed
    .filter((m) => !m.error.hasError && m.findings.severity !== "clean" && m.findings.severity !== "info")
    .sort((a, b) => {
      const order: Record<FindingSeverity, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4, clean: 5 };
      return order[a.findings.severity] - order[b.findings.severity];
    });

  const clean = analyzed.filter((m) => !m.error.hasError && (m.findings.severity === "clean" || m.findings.severity === "info"));
  const errors = analyzed.filter((m) => m.error.hasError);

  const severityColors: Record<FindingSeverity, string> = {
    critical: "text-red-400 bg-red-500/10 border-red-500/30",
    high: "text-orange-400 bg-orange-500/10 border-orange-500/30",
    medium: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    low: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
    info: "text-slate-400 bg-slate-500/10 border-slate-500/30",
    clean: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  };

  return (
    <div className="space-y-6">
      {/* Needs Attention */}
      {withIssues.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            Needs Attention ({withIssues.length})
          </h3>
          <div className="space-y-2">
            {withIssues.map(({ module, findings }) => {
              const Icon = MODULE_ICONS[module] || FileCode;
              const colors = severityColors[findings.severity];
              return (
                <div key={module} className={`flex items-center gap-3 p-3 rounded-lg border ${colors}`}>
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="font-medium text-sm flex-1">{formatModuleName(module)}</span>
                  <span className="text-xs text-muted-foreground">{getModuleCategoryLabel(module)}</span>
                  <Badge variant="outline" className={`text-xs font-mono ${colors}`}>
                    {findings.criticalCount > 0 && <span className="text-red-400">{findings.criticalCount} critical</span>}
                    {findings.criticalCount > 0 && findings.highCount > 0 && <span className="mx-1">·</span>}
                    {findings.highCount > 0 && <span className="text-orange-400">{findings.highCount} high</span>}
                    {findings.criticalCount === 0 && findings.highCount === 0 && <span>{findings.count} findings</span>}
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <XCircle className="h-4 w-4 text-red-400" />
            Errors ({errors.length})
          </h3>
          <div className="space-y-2">
            {errors.map(({ module, error }) => {
              const Icon = MODULE_ICONS[module] || FileCode;
              return (
                <div key={module} className="flex items-center gap-3 p-3 rounded-lg border border-red-500/30 bg-red-500/5">
                  <Icon className="h-4 w-4 text-red-400 flex-shrink-0" />
                  <span className="font-medium text-sm">{formatModuleName(module)}</span>
                  <span className="text-xs text-red-400/70 truncate flex-1 text-right font-mono">{error.errorMessage}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All Clear */}
      {clean.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            All Clear ({clean.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {clean.map(({ module }) => {
              const Icon = MODULE_ICONS[module] || FileCode;
              return (
                <Badge key={module} variant="outline" className="px-2.5 py-1.5 gap-1.5 border-emerald-500/20 text-emerald-400 bg-emerald-500/5">
                  <Icon className="h-3 w-3" />
                  {formatModuleName(module)}
                  <CheckCircle2 className="h-3 w-3" />
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {withIssues.length === 0 && errors.length === 0 && clean.length === 0 && (
        <p className="text-center text-muted-foreground py-8">No module data available.</p>
      )}
    </div>
  );
}

export default function ScanDetails() {
  const { t } = useTranslation("scans");
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  // Get tab from URL query param (for navigation from Simple view)
  const tabFromUrl = searchParams.get("tab");

  const { data: scan, isLoading: scanLoading } = useQuery({
    queryKey: ["scan", id],
    queryFn: () => scansApi.get(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const scan = query.state.data;
      if (scan?.status === "running" || scan?.status === "pending") {
        return 1000; // Poll every 1 second for responsive progress updates
      }
      return false;
    },
  });

  const { data: results, isLoading: resultsLoading } = useQuery({
    queryKey: ["scanResults", id],
    queryFn: () => scansApi.getResults(id!),
    enabled: !!id && (scan?.status === "completed" || scan?.status === "failed"),
  });

  if (scanLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="flex-1">
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-96 w-full rounded-lg" />
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <div className="p-4 rounded-xl bg-muted/30 border border-border mb-6">
          <AlertTriangle className="h-12 w-12 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-2">{t("details.notFound", "Scan not found")}</h3>
        <p className="text-muted-foreground text-sm mb-6">
          The scan you're looking for doesn't exist or has been deleted.
        </p>
        <Button asChild className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold">
          <Link to="/scans">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("details.backToScans", "Back to Scans")}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => window.history.back()}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("details.backToScans", "Back to Scans")}
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <Radar className="h-8 w-8 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-mono">{scan.target}</h1>
              <p className="text-muted-foreground text-sm">
                Started {new Date(scan.created_at).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ViewToggle id={id!} currentView="advanced" />
            <StatusBadge status={scan.status} />
          </div>
        </div>
      </div>

      {/* Progress indicator for running scans */}
      {scan.status === "running" && scan.progress && (
        <ProgressIndicator progress={scan.progress} />
      )}

      {/* Error display for failed scans */}
      {scan.status === "failed" && scan.error && (
        <div className=" rounded-lg border border-red-500/30 bg-red-500/5 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-semibold text-red-50">Scan Failed</h3>
              <p className="text-sm text-red-400/80">
                Some modules encountered errors during execution
              </p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground bg-muted/50 rounded p-3 font-mono">
            {scan.error}
          </p>
        </div>
      )}

      {/* Scan configuration — collapsible, collapsed by default */}
      <CollapsibleScanConfig scan={scan} results={results} t={t} />

      {/* Results section */}
      {(scan.status === "completed" || scan.status === "failed") && (
        <div className=" rounded-lg border border-border bg-card/50 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="h-6 w-1 rounded-full bg-cyan-500" />
              <div>
                <h2 className="text-lg font-semibold">{t("details.results.title", "Results")}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {scan.status === "failed"
                    ? "Partial results from successful modules"
                    : t("details.results.description", "Scan findings and data")}
                </p>
              </div>
            </div>
            {results && (
              <AdvancedExportMenu scan={scan} results={results} />
            )}
          </div>

          {resultsLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-24 rounded-lg" />
              ))}
            </div>
          ) : results?.modules ? (
            <Tabs
              defaultValue={
                // If tab specified in URL (from Simple view navigation), use it
                tabFromUrl && (tabFromUrl === "insights" || tabFromUrl === "overview" || tabFromUrl in results.modules)
                  ? tabFromUrl
                  : results.ai_insights
                    ? "insights"
                    : "overview"
              }
              className="space-y-6"
            >
              <TabsList className="bg-muted p-1.5 flex-wrap relative z-10 gap-1 mb-4 h-auto rounded-lg">
                {/* AI Insights tab - show first if available */}
                {results.ai_insights && (
                  <TabsTrigger
                    value="insights"
                    className="data-[state=active]:bg-background gap-1.5"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Insights
                    <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-cyan-500/20 text-cyan-400">
                      {results.ai_insights.summary.total_findings}
                    </span>
                  </TabsTrigger>
                )}
                <TabsTrigger value="overview" className="data-[state=active]:bg-background">
                  {t("details.results.overview", "Overview")}
                </TabsTrigger>

                {/* Module tabs grouped by business category, sorted by severity within groups */}
                {MODULE_CATEGORIES.map((category) => {
                  // Get modules that exist in results for this category
                  const categoryModules = category.modules
                    .filter((m) => m in results.modules)
                    .sort((a, b) => {
                      const aFindings = analyzeModuleFindings(a, results.modules[a]);
                      const bFindings = analyzeModuleFindings(b, results.modules[b]);
                      const order: Record<FindingSeverity, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4, clean: 5 };
                      return order[aFindings.severity] - order[bFindings.severity];
                    });

                  if (categoryModules.length === 0) return null;

                  return (
                    <span key={category.label} className="contents">
                      {/* Category separator */}
                      <span className="flex items-center px-1.5 text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold select-none">
                        {category.label}
                      </span>
                      {categoryModules.map((module) => {
                        const Icon = MODULE_ICONS[module] || FileCode;
                        return (
                          <TabsTrigger
                            key={module}
                            value={module}
                            className="data-[state=active]:bg-background gap-1.5"
                          >
                            <Icon className="h-3.5 w-3.5 opacity-60" />
                            {formatModuleName(module)}
                            <ModuleFindingsBadge module={module} data={results.modules[module]} />
                          </TabsTrigger>
                        );
                      })}
                    </span>
                  );
                })}

                {/* Any modules not in a category */}
                {Object.keys(results.modules)
                  .filter((m) => !MODULE_CATEGORIES.some((c) => c.modules.includes(m)))
                  .map((module) => {
                    const Icon = MODULE_ICONS[module] || FileCode;
                    return (
                      <TabsTrigger
                        key={module}
                        value={module}
                        className="data-[state=active]:bg-background gap-1.5"
                      >
                        <Icon className="h-3.5 w-3.5 opacity-60" />
                        {formatModuleName(module)}
                        <ModuleFindingsBadge module={module} data={results.modules[module]} />
                      </TabsTrigger>
                    );
                  })}
              </TabsList>

              {/* Module Errors Summary - shows which modules failed */}
              {(() => {
                const moduleErrors = getModuleErrors(results.modules);
                if (moduleErrors.length === 0) return null;

                return (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-amber-500/20">
                        <AlertTriangle className="h-5 w-5 text-amber-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-amber-50">
                          {moduleErrors.length} Module{moduleErrors.length > 1 ? "s" : ""} Failed
                        </h3>
                        <p className="text-sm text-amber-400/80">
                          The following modules encountered errors during scanning
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2 mt-4">
                      {moduleErrors.map(({ module, error }) => {
                        const Icon = MODULE_ICONS[module] || FileCode;
                        return (
                          <div
                            key={module}
                            className="flex items-start gap-3 p-3 rounded-md bg-muted/50 border border-border"
                          >
                            <div className="p-1.5 rounded bg-red-500/20 shrink-0">
                              <Icon className="h-3.5 w-3.5 text-red-400" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm text-foreground">
                                {formatModuleName(module)}
                              </p>
                              <p className="text-xs text-red-400/80 mt-0.5 font-mono break-all">
                                {error}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* AI Insights tab content */}
              {results.ai_insights && (
                <TabsContent value="insights" className="space-y-6">
                  <RiskOverview insights={results.ai_insights} />
                  {results.ai_insights.findings.length > 0 && (
                    <FindingsList findings={results.ai_insights.findings} />
                  )}
                </TabsContent>
              )}

              <TabsContent value="overview">
                <OverviewContent modules={results.modules} />
              </TabsContent>

              {Object.entries(results.modules).map(([module, data]) => (
                <TabsContent key={module} value={module}>
                  <ModuleVisualization module={module} data={data} allModules={results.modules} />
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {t("details.results.noResults", "No results available")}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
